import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, MessageCircle, Send } from 'lucide-react';
import { ToolPageShell } from '../components/tool';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useCredits } from '../hooks/useCredits';
import { safeInvoke } from '../lib/safeInvoke';
import { PhaseLoader } from '../components/shared/PhaseLoader';
import { ReadingFeedback } from '../components/shared/ReadingFeedback';
import { ReadingActions } from '../components/shared/ReadingActions';
import { EmptyState } from '../components/ui/EmptyState';
import { getSignEmoji } from '../lib/astrology';
import { useI18n } from '../hooks/useI18n';
import { usePricing } from '../contexts/PricingContext';
import { useAvulsoProduct } from '../hooks/useAvulsoProducts';
import { AvulsoCompareModal } from '../components/shared/AvulsoCompareModal';

const CATEGORIES = ['Livre', 'Amor', 'Trabalho', 'Saúde', 'Família', 'Espiritual', 'Finanças'];

const EXAMPLES: Record<string, string[]> = {
  Livre:     ['O que os astros dizem sobre meu momento atual?', 'Quais são meus maiores desafios este ano?', 'Que oportunidades estão chegando para mim?'],
  Amor:      ['Estamos no caminho certo juntos?', 'Por que repetindo padrões em relacionamentos?', 'O que devo trabalhar em mim para atrair amor?'],
  Trabalho:  ['Devo mudar de emprego agora?', 'O que me bloqueia de crescer profissionalmente?', 'Quando é um bom período para empreender?'],
  Saúde:     ['Como posso melhorar minha energia?', 'Que cuidados devo ter neste período?', 'O que os astros dizem sobre meu bem-estar?'],
  Família:   ['Como melhorar minha relação com minha família?', 'Que padrões familiares devo quebrar?', 'Como posso apoiar alguém que amo?'],
  Espiritual:['Qual é o meu propósito nesta vida?', 'Como me conectar mais com minha intuição?', 'O que os astros revelam sobre minha missão?'],
  Finanças:  ['Quando é um bom período para investir?', 'O que me bloqueia de prosperar?', 'Como atrair mais abundância?'],
};

interface HistoryItem {
  id: string;
  question: string;
  answer: string;
  category: string;
  created_at: string;
}

const LOADING_PHASES = [
  'Consultando os trânsitos planetários...',
  'Analisando seu mapa natal...',
  'Formulando a resposta dos astros...',
  'Preparando sua leitura personalizada...',
];

export function QuestionsPage() {
  const { profile }             = useAuth();
  const { credits, isUnlimited, refresh, creditsError } = useCredits();
  const navigate                = useNavigate();
  const { t, lang }             = useI18n();
  const { formatProductPrice, getProduct }  = usePricing();
  const textareaRef             = useRef<HTMLTextAreaElement>(null);
  const avulsoProduct           = useAvulsoProduct('pergunte-aos-astros');

  const [category,    setCategory]   = useState('Livre');
  const [question,    setQuestion]   = useState('');
  const [loading,     setLoading]    = useState(false);
  const [answer,      setAnswer]     = useState('');
  const [questionId,  setQuestionId] = useState<string | null>(null);
  const [error,       setError]      = useState<string | null>(null);
  const [showExamples,setShowExamples]= useState(false);
  const [history,     setHistory]    = useState<HistoryItem[]>([]);
  const [expandedHistory, setExpandedHistory] = useState<string | null>(null);
  const [showAvulsoModal, setShowAvulsoModal] = useState(false);

  useEffect(() => {
    if (profile?.id) loadHistory();
  }, [profile?.id]);

  useEffect(() => {
    if (answer) { setAnswer(''); }
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadHistory() {
    if (!profile?.id) return;
    const { data } = await supabase
      .from('astrological_questions')
      .select('id, question, answer, created_at')
      .eq('user_id', profile.id)
      .eq('status', 'completed')
      .order('created_at', { ascending: false })
      .limit(20);
    if (data) setHistory(data.map(d => ({ ...d, category: 'Livre' })));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!(question ?? '').trim() || loading) return;

    setLoading(true);
    setAnswer('');
    setError(null);
    setQuestionId(null);

    const { data, fromFallback } = await safeInvoke<{ answer: string; questionId: string | null }>('questions', {
      body:     { question: (question ?? '').trim(), category },
      fallback: { answer: '', questionId: null },
      timeout:  30_000,
    });

    setLoading(false);

    if (fromFallback || !data.answer) {
      setError(data.answer || t('pages.iching.error'));
      return;
    }

    setAnswer(data.answer);
    if (data.questionId) setQuestionId(data.questionId);
    refresh();
    loadHistory();
  }

  const signEmoji = getSignEmoji(profile?.sun_sign ?? '');

  if (!profile?.birth_date) {
    return (
      <div className="page-container">
        <EmptyState
          emoji="⭐"
          title={t('pages.questions.emptyTitle')}
          subtitle={t('pages.questions.emptySubtitle')}
          cta={t('pages.questions.emptyCta')}
          ctaAction={() => navigate('/profile')}
        />
      </div>
    );
  }

  if (creditsError) return (
    <div className="page-container" style={{ textAlign: 'center', paddingTop: '3rem' }}>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>Erro ao carregar créditos.</p>
      <button className="btn btn-secondary btn-sm" onClick={refresh}>Tentar novamente</button>
    </div>
  );

  const noCredits = !isUnlimited && credits.questions <= 0;

  return (
    <ToolPageShell
      icon={MessageCircle}
      title={t('pages.questions.title')}
    >

      {/* Credit status */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <span className="credit-pill">💬 {isUnlimited ? '∞' : credits.questions} crédito{!isUnlimited && credits.questions === 1 ? '' : 's'} disponível{!isUnlimited && credits.questions === 1 ? '' : 'is'}</span>
        {noCredits && (
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/wallet')}>
            Comprar créditos ✦
          </button>
        )}
      </div>

      {/* No credits, show purchase inline */}
      {noCredits ? (
        <div className="card" style={{ marginBottom: '1.5rem', border: '1px solid var(--border-gold)', backgroundColor: 'rgba(31,78,95,0.06)' }}>
          <p style={{ color: 'var(--text-primary)', fontWeight: 600, marginBottom: '0.5rem', fontSize: 'var(--text-label)' }}>
            {t('pages.questions.noCreditsTitle')}
          </p>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-caption)', marginBottom: '1rem' }}>
            {t('pages.questions.noCreditsDesc')}
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: avulsoProduct ? '0.75rem' : 0 }}>
            {[{ label: '3 perguntas', key: 'questions_q3' }, { label: '5 perguntas', key: 'questions_q5' }, { label: '10 perguntas', key: 'questions_q10' }].map(opt => (
              <button key={opt.key} className="btn btn-primary btn-sm" onClick={() => navigate('/wallet')}>
                {opt.label}, {formatProductPrice(opt.key)}
              </button>
            ))}
          </div>
          {avulsoProduct && (
            <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: 0 }}>
                Ou compre esta pergunta de forma avulsa por <strong style={{ color: 'var(--petrol)' }}>R${avulsoProduct.price_brl.toFixed(2).replace('.', ',')}</strong>
              </p>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowAvulsoModal(true)}>
                Ver comparativo de planos
              </button>
            </div>
          )}
        </div>
      ) : (
        /* Question form */
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <form onSubmit={handleSubmit}>
            {/* Category chips */}
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setCategory(cat)}
                  className={category === cat ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Textarea */}
            <div className="form-group">
              <label className="form-label" htmlFor="question">{t('pages.questions.questionLabel')}</label>
              <textarea
                id="question"
                ref={textareaRef}
                className="form-textarea"
                value={question}
                onChange={e => setQuestion(e.target.value)}
                placeholder={t('pages.questions.questionPlaceholder')}
                rows={4}
                maxLength={500}
                style={{ minHeight: '100px' }}
              />
              <p className="form-hint" style={{ textAlign: 'right' }}>{question.length}/500</p>
            </div>

            {/* Example questions toggle */}
            <button type="button" className="btn btn-ghost btn-sm"
              style={{ marginBottom: '1rem' }}
              onClick={() => setShowExamples(v => !v)}>
              {showExamples ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {showExamples ? t('pages.questions.hideExamples') : t('pages.questions.showExamples')}
            </button>

            {showExamples && (
              <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '1rem' }}>
                {(EXAMPLES[category] ?? EXAMPLES['Livre']).map(ex => (
                  <button
                    key={ex}
                    type="button"
                    onClick={() => { setQuestion(ex); textareaRef.current?.focus(); setShowExamples(false); }}
                    style={{ textAlign: 'left', background: 'var(--bg-base)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '0.625rem 0.875rem', color: 'var(--text-secondary)', fontSize: 'var(--text-caption)', cursor: 'pointer', transition: 'border-color 150ms' }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--border-gold)')}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border-subtle)')}
                  >
                    {ex}
                  </button>
                ))}
              </div>
            )}

            {/* Disclaimer */}
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '1rem', lineHeight: 1.5 }}>
              {t('pages.questions.disclaimer')}
            </p>

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={loading || !(question ?? '').trim()}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}
            >
              {loading ? t('pages.questions.submitLoading') : <><Send size={16} /> {t('pages.questions.submitCta')}</>}
            </button>
          </form>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <PhaseLoader phases={LOADING_PHASES} />
        </div>
      )}

      {/* Answer */}
      {answer && !loading && (
        <div className="card animate-fade-in" style={{ marginBottom: '1.5rem' }}>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>
            {t('pages.questions.answerTitle')}
          </p>
          <div className="reading-text">
            {answer.split('\n').filter(Boolean).map((para, i) => <p key={i}>{para}</p>)}
          </div>
          <ReadingActions pageName="Pergunte aos Astros" reading={answer} signEmoji={signEmoji} />
          <ReadingFeedback feature="questions" readingId={questionId ?? undefined} />
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div style={{ padding: '1rem', backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 'var(--radius-md)', color: '#fca5a5', fontSize: 'var(--text-label)', marginBottom: '1.5rem' }}>
          {error}
        </div>
      )}

      {/* History accordion */}
      {history.length > 0 && (
        <section>
          <h2 className="font-display" style={{ fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '1rem' }}>
            {t('pages.questions.historyTitle')}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {history.map(item => (
              <div key={item.id} className="card" style={{ padding: '0' }}>
                <button
                  style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}
                  onClick={() => setExpandedHistory(expandedHistory === item.id ? null : item.id)}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.25rem', fontSize: 'var(--text-label)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.question}
                    </p>
                    <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>
                      {new Date(item.created_at).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  {expandedHistory === item.id ? <ChevronUp size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} /> : <ChevronDown size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}
                </button>
                {expandedHistory === item.id && (
                  <div className="animate-fade-in" style={{ padding: '0 1rem 1rem', borderTop: '1px solid var(--border-subtle)' }}>
                    <div className="reading-text" style={{ marginTop: '0.75rem', fontSize: 'var(--text-label)' }}>
                      {(item.answer || '').split('\n').filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
      {showAvulsoModal && avulsoProduct && (
        <AvulsoCompareModal
          serviceName="Perguntar aos Astros"
          serviceSlug="pergunte-aos-astros"
          avulsoPrice={avulsoProduct.price_brl}
          avulsoDesc={avulsoProduct.description}
          monthlyProd={getProduct('monthly_sub')}
          annualProd={getProduct('annual_sub')}
          onClose={() => setShowAvulsoModal(false)}
        />
      )}
    </ToolPageShell>
  );
}
